import React, { useState, useEffect } from 'react';
import { Home, DollarSign, Percent, Calendar, TrendingUp, Info } from 'lucide-react';

const PropertyInfo = ({ initialData = {}, onChange }) => {
    const [formData, setFormData] = useState({
        homePrice: initialData.homePrice || 200000,
        downPayment: initialData.downPayment || 40000,
        downPaymentPercent: initialData.downPaymentPercent || 20,
        interestRate: initialData.interestRate || 5.5,
        loanType: initialData.loanType || '15-Year Fixed',
        zipCode: initialData.zipCode || '',
        propertyTax: initialData.propertyTax || 1.1,
        homeInsurance: initialData.homeInsurance || 900,
        monthlyHOA: initialData.monthlyHOA || 0,
        otherMonthlyDebts: initialData.otherMonthlyDebts || 0,
        removeMortgageInsurance: initialData.removeMortgageInsurance || false
    });

    const [monthlyPayment, setMonthlyPayment] = useState(0);
    const [breakdown, setBreakdown] = useState({
        principalAndInterest: 0,
        propertyTaxes: 0,
        homeInsurance: 0,
        mortgageInsurance: 0,
        hoa: 0,
        otherDebts: 0
    });

    // Calculate mortgage payment
    useEffect(() => {
        const principal = formData.homePrice - formData.downPayment;
        const monthlyRate = formData.interestRate / 100 / 12;
        const loanTermMonths = formData.loanType === '15-Year Fixed' ? 180 : 360;

        // Monthly principal & interest
        let principalAndInterest = 0;
        if (monthlyRate > 0) {
            principalAndInterest = principal * (monthlyRate * Math.pow(1 + monthlyRate, loanTermMonths)) /
                (Math.pow(1 + monthlyRate, loanTermMonths) - 1);
        } else {
            principalAndInterest = principal / loanTermMonths;
        }

        // Property taxes (annual / 12)
        const propertyTaxes = (formData.homePrice * (formData.propertyTax / 100)) / 12;

        // Home insurance (monthly)
        const homeInsuranceMonthly = formData.homeInsurance;

        // Mortgage insurance (if down payment < 20%)
        let mortgageInsurance = 0;
        if (!formData.removeMortgageInsurance && formData.downPaymentPercent < 20) {
            mortgageInsurance = principal * 0.005 / 12; // Approximate 0.5% annual PMI
        }

        const total = principalAndInterest + propertyTaxes + homeInsuranceMonthly +
            mortgageInsurance + formData.monthlyHOA + formData.otherMonthlyDebts;

        setMonthlyPayment(total);
        setBreakdown({
            principalAndInterest,
            propertyTaxes,
            homeInsurance: homeInsuranceMonthly,
            mortgageInsurance,
            hoa: formData.monthlyHOA,
            otherDebts: formData.otherMonthlyDebts
        });

        // Notify parent component
        if (onChange) {
            onChange({
                ...formData,
                monthlyPayment: total,
                breakdown: {
                    principalAndInterest,
                    propertyTaxes,
                    homeInsurance: homeInsuranceMonthly,
                    mortgageInsurance,
                    hoa: formData.monthlyHOA,
                    otherDebts: formData.otherMonthlyDebts
                }
            });
        }
    }, [formData]);

    const handleInputChange = (field, value) => {
        const newData = { ...formData };

        if (field === 'homePrice') {
            const price = parseFloat(value) || 0;
            newData.homePrice = price;
            // Recalculate down payment based on percentage
            newData.downPayment = Math.round(price * (newData.downPaymentPercent / 100));
        } else if (field === 'downPayment') {
            const payment = parseFloat(value) || 0;
            newData.downPayment = payment;
            // Recalculate percentage
            newData.downPaymentPercent = newData.homePrice > 0
                ? Math.round((payment / newData.homePrice) * 100 * 10) / 10
                : 0;
        } else if (field === 'downPaymentPercent') {
            const percent = parseFloat(value) || 0;
            newData.downPaymentPercent = Math.min(100, Math.max(0, percent));
            // Recalculate down payment
            newData.downPayment = Math.round(newData.homePrice * (newData.downPaymentPercent / 100));
        } else {
            newData[field] = value;
        }

        setFormData(newData);
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const InfoTooltip = ({ text }) => (
        <div className="group relative inline-block ml-1">
            <Info className="w-4 h-4 text-slate-500 cursor-help" />
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-48 p-2 dark:bg-slate-800 bg-slate-900 text-white text-xs rounded shadow-lg z-10">
                {text}
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="border-b dark:border-slate-800 border-slate-200 pb-4">
                <h3 className="text-xl font-bold dark:text-white text-slate-900 mb-2">Calculate your monthly mortgage payments</h3>
                <p className="dark:text-yellow-400 text-yellow-600 text-lg font-semibold">
                    Your est. payment: {formatCurrency(monthlyPayment)}/month
                </p>
            </div>

            {/* Main Calculator Grid */}
            <div className="dark:bg-slate-900/50 bg-slate-50 border dark:border-slate-800 border-slate-200 rounded-xl p-6 space-y-6">
                {/* Row 1: Home Price, Down Payment, Interest Rate */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Home Price */}
                    <div>
                        <label className="flex items-center text-sm font-semibold dark:text-slate-300 text-slate-700 mb-2">
                            <Home className="w-4 h-4 mr-1" />
                            Home Price
                            <InfoTooltip text="The total purchase price of the property" />
                        </label>
                        <input
                            type="number"
                            value={formData.homePrice}
                            onChange={(e) => handleInputChange('homePrice', e.target.value)}
                            className="w-full dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-300 rounded-lg px-4 py-2.5 dark:text-white text-slate-900 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        />
                        <input
                            type="range"
                            min="50000"
                            max="2000000"
                            step="10000"
                            value={formData.homePrice}
                            onChange={(e) => handleInputChange('homePrice', e.target.value)}
                            className="w-full mt-2 accent-yellow-500"
                        />
                    </div>

                    {/* Down Payment */}
                    <div>
                        <label className="flex items-center text-sm font-semibold dark:text-slate-300 text-slate-700 mb-2">
                            <DollarSign className="w-4 h-4 mr-1" />
                            Down Payment
                            <InfoTooltip text="Amount paid upfront, typically 20% to avoid PMI" />
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="number"
                                value={formData.downPayment}
                                onChange={(e) => handleInputChange('downPayment', e.target.value)}
                                className="flex-1 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-300 rounded-lg px-4 py-2.5 dark:text-white text-slate-900 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                            />
                            <div className="relative">
                                <input
                                    type="number"
                                    value={formData.downPaymentPercent}
                                    onChange={(e) => handleInputChange('downPaymentPercent', e.target.value)}
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    className="w-20 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-300 rounded-lg px-3 py-2.5 dark:text-white text-slate-900 focus:ring-2 focus:ring-yellow-500 focus:border-transparent pr-8"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
                            </div>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max={formData.homePrice}
                            step="1000"
                            value={formData.downPayment}
                            onChange={(e) => handleInputChange('downPayment', e.target.value)}
                            className="w-full mt-2 accent-yellow-500"
                        />
                    </div>

                    {/* Interest Rate */}
                    <div>
                        <label className="flex items-center text-sm font-semibold dark:text-slate-300 text-slate-700 mb-2">
                            <Percent className="w-4 h-4 mr-1" />
                            Interest Rate
                            <InfoTooltip text="Annual interest rate for your mortgage" />
                        </label>
                        <div className="relative">
                            <input
                                type="number"
                                value={formData.interestRate}
                                onChange={(e) => handleInputChange('interestRate', e.target.value)}
                                step="0.01"
                                min="0"
                                max="20"
                                className="w-full dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-300 rounded-lg px-4 py-2.5 dark:text-white text-slate-900 focus:ring-2 focus:ring-yellow-500 focus:border-transparent pr-8"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
                        </div>
                        <input
                            type="range"
                            min="2"
                            max="10"
                            step="0.1"
                            value={formData.interestRate}
                            onChange={(e) => handleInputChange('interestRate', e.target.value)}
                            className="w-full mt-2 accent-yellow-500"
                        />
                        <button
                            onClick={() => handleInputChange('interestRate', 5.5)}
                            className="text-xs dark:text-yellow-400 text-yellow-600 hover:text-yellow-500 dark:hover:text-yellow-300 mt-1"
                        >
                            Reset
                        </button>
                    </div>
                </div>

                {/* Row 2: Loan Type, Zip Code, Property Tax */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Loan Type */}
                    <div>
                        <label className="flex items-center text-sm font-semibold dark:text-slate-300 text-slate-700 mb-2">
                            <Calendar className="w-4 h-4 mr-1" />
                            Loan Type
                            <InfoTooltip text="Choose your mortgage term length" />
                        </label>
                        <select
                            value={formData.loanType}
                            onChange={(e) => handleInputChange('loanType', e.target.value)}
                            className="w-full dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-300 rounded-lg px-4 py-2.5 dark:text-white text-slate-900 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        >
                            <option value="15-Year Fixed">15-Year Fixed</option>
                            <option value="30-Year Fixed">30-Year Fixed</option>
                            <option value="20-Year Fixed">20-Year Fixed</option>
                            <option value="10-Year Fixed">10-Year Fixed</option>
                        </select>
                    </div>

                    {/* Zip Code */}
                    <div>
                        <label className="flex items-center text-sm font-semibold dark:text-slate-300 text-slate-700 mb-2">
                            Zip code
                            <InfoTooltip text="Property location for tax calculations" />
                        </label>
                        <input
                            type="text"
                            value={formData.zipCode}
                            onChange={(e) => handleInputChange('zipCode', e.target.value)}
                            placeholder="93955"
                            maxLength="5"
                            className="w-full dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-300 rounded-lg px-4 py-2.5 dark:text-white text-slate-900 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        />
                    </div>

                    {/* Property Tax */}
                    <div>
                        <label className="flex items-center text-sm font-semibold dark:text-slate-300 text-slate-700 mb-2">
                            <TrendingUp className="w-4 h-4 mr-1" />
                            Property Tax
                            <InfoTooltip text="Annual property tax rate as percentage of home value" />
                        </label>
                        <div className="relative">
                            <input
                                type="number"
                                value={formData.propertyTax}
                                onChange={(e) => handleInputChange('propertyTax', e.target.value)}
                                step="0.1"
                                min="0"
                                max="10"
                                className="w-full dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-300 rounded-lg px-4 py-2.5 dark:text-white text-slate-900 focus:ring-2 focus:ring-yellow-500 focus:border-transparent pr-8"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="5"
                            step="0.1"
                            value={formData.propertyTax}
                            onChange={(e) => handleInputChange('propertyTax', e.target.value)}
                            className="w-full mt-2 accent-yellow-500"
                        />
                    </div>
                </div>

                {/* Row 3: Home Insurance, Monthly HOA, Other Debts */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Home Insurance */}
                    <div>
                        <label className="flex items-center text-sm font-semibold dark:text-slate-300 text-slate-700 mb-2">
                            Home Insurance
                            <InfoTooltip text="Monthly home insurance premium" />
                        </label>
                        <input
                            type="number"
                            value={formData.homeInsurance}
                            onChange={(e) => handleInputChange('homeInsurance', e.target.value)}
                            className="w-full dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-300 rounded-lg px-4 py-2.5 dark:text-white text-slate-900 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        />
                        <input
                            type="range"
                            min="0"
                            max="500"
                            step="10"
                            value={formData.homeInsurance}
                            onChange={(e) => handleInputChange('homeInsurance', e.target.value)}
                            className="w-full mt-2 accent-yellow-500"
                        />
                    </div>

                    {/* Monthly HOA */}
                    <div>
                        <label className="flex items-center text-sm font-semibold dark:text-slate-300 text-slate-700 mb-2">
                            Monthly HOA Due
                            <InfoTooltip text="Homeowners Association monthly fees" />
                        </label>
                        <input
                            type="number"
                            value={formData.monthlyHOA}
                            onChange={(e) => handleInputChange('monthlyHOA', parseFloat(e.target.value) || 0)}
                            className="w-full dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-300 rounded-lg px-4 py-2.5 dark:text-white text-slate-900 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        />
                        <input
                            type="range"
                            min="0"
                            max="1000"
                            step="10"
                            value={formData.monthlyHOA}
                            onChange={(e) => handleInputChange('monthlyHOA', parseFloat(e.target.value) || 0)}
                            className="w-full mt-2 accent-yellow-500"
                        />
                    </div>

                    {/* Other Monthly Debts */}
                    <div>
                        <label className="flex items-center text-sm font-semibold dark:text-slate-300 text-slate-700 mb-2">
                            Other Monthly Debts
                            <InfoTooltip text="Car loans, student loans, credit cards, etc." />
                        </label>
                        <input
                            type="number"
                            value={formData.otherMonthlyDebts}
                            onChange={(e) => handleInputChange('otherMonthlyDebts', parseFloat(e.target.value) || 0)}
                            className="w-full dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-300 rounded-lg px-4 py-2.5 dark:text-white text-slate-900 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        />
                        <input
                            type="range"
                            min="0"
                            max="2000"
                            step="50"
                            value={formData.otherMonthlyDebts}
                            onChange={(e) => handleInputChange('otherMonthlyDebts', parseFloat(e.target.value) || 0)}
                            className="w-full mt-2 accent-yellow-500"
                        />
                    </div>
                </div>

                {/* Remove Mortgage Insurance Checkbox */}
                <div className="flex items-center gap-2 pt-2">
                    <input
                        type="checkbox"
                        id="removeMortgageInsurance"
                        checked={formData.removeMortgageInsurance}
                        onChange={(e) => handleInputChange('removeMortgageInsurance', e.target.checked)}
                        className="w-4 h-4 text-yellow-600 dark:bg-slate-800 bg-white dark:border-slate-700 border-slate-300 rounded focus:ring-yellow-500"
                    />
                    <label htmlFor="removeMortgageInsurance" className="text-sm dark:text-slate-300 text-slate-700 flex items-center">
                        Remove Mortgage Insurance
                        <InfoTooltip text="Typically required if down payment is less than 20%" />
                    </label>
                </div>
            </div>

            {/* Payment Breakdown */}
            <div className="dark:bg-slate-900/50 bg-slate-50 dark:border-slate-800 border-slate-200 border rounded-xl p-6">
                <h4 className="text-lg font-bold dark:text-white text-slate-900 mb-4">Payment Breakdown</h4>

                {/* Donut Chart Visualization */}
                <div className="flex items-center justify-center mb-6">
                    <div className="relative w-48 h-48">
                        <svg viewBox="0 0 200 200" className="w-full h-full">
                            {(() => {
                                const total = monthlyPayment || 1;
                                const centerX = 100;
                                const centerY = 100;
                                const radius = 70;
                                const strokeWidth = 30;

                                // Calculate percentages
                                const segments = [
                                    {
                                        value: breakdown.principalAndInterest,
                                        color: '#1e40af',
                                        label: 'Principal & Interest'
                                    },
                                    {
                                        value: breakdown.propertyTaxes,
                                        color: '#fbbf24',
                                        label: 'Property Taxes'
                                    },
                                    {
                                        value: breakdown.homeInsurance,
                                        color: '#a3e635',
                                        label: 'Home Insurance'
                                    },
                                    {
                                        value: breakdown.mortgageInsurance,
                                        color: '#c084fc',
                                        label: 'Mortgage Insurance'
                                    },
                                    {
                                        value: breakdown.hoa,
                                        color: '#fbbf24',
                                        label: 'HOA Fees'
                                    },
                                    {
                                        value: breakdown.otherDebts,
                                        color: '#ef4444',
                                        label: 'Other Debts'
                                    }
                                ].filter(seg => seg.value > 0);

                                let currentAngle = -90; // Start from top

                                const polarToCartesian = (angle) => {
                                    const rad = (angle * Math.PI) / 180;
                                    return {
                                        x: centerX + radius * Math.cos(rad),
                                        y: centerY + radius * Math.sin(rad)
                                    };
                                };

                                const createArc = (startAngle, endAngle, color) => {
                                    const start = polarToCartesian(startAngle);
                                    const end = polarToCartesian(endAngle);
                                    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

                                    const pathData = [
                                        `M ${start.x} ${start.y}`,
                                        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`
                                    ].join(' ');

                                    return (
                                        <path
                                            key={`${startAngle}-${color}`}
                                            d={pathData}
                                            fill="none"
                                            stroke={color}
                                            strokeWidth={strokeWidth}
                                            strokeLinecap="butt"
                                        />
                                    );
                                };

                                return segments.map((segment) => {
                                    const percentage = segment.value / total;
                                    const angle = percentage * 360;
                                    const arc = createArc(currentAngle, currentAngle + angle, segment.color);
                                    currentAngle += angle;
                                    return arc;
                                });
                            })()}
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <div className="text-3xl font-bold dark:text-white text-slate-900">{formatCurrency(monthlyPayment)}</div>
                            <div className="text-xs dark:text-slate-400 text-slate-500">/month</div>
                        </div>
                    </div>
                </div>

                {/* Legend */}
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded-full bg-yellow-800"></div>
                            <span className="text-sm dark:text-slate-300 text-slate-600">Principal & Interest</span>
                        </div>
                        <span className="text-sm font-semibold dark:text-white text-slate-900">{formatCurrency(breakdown.principalAndInterest)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded-full bg-cyan-600"></div>
                            <span className="text-sm dark:text-slate-300 text-slate-600">Property Taxes</span>
                        </div>
                        <span className="text-sm font-semibold dark:text-white text-slate-900">{formatCurrency(breakdown.propertyTaxes)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded-full bg-lime-400"></div>
                            <span className="text-sm dark:text-slate-300 text-slate-600">Home Insurance</span>
                        </div>
                        <span className="text-sm font-semibold dark:text-white text-slate-900">{formatCurrency(breakdown.homeInsurance)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded-full bg-purple-400"></div>
                            <span className="text-sm dark:text-slate-300 text-slate-600">Mortgage Insurance</span>
                        </div>
                        <span className="text-sm font-semibold dark:text-white text-slate-900">{formatCurrency(breakdown.mortgageInsurance)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                            <span className="text-sm dark:text-slate-300 text-slate-600">HOA Fees</span>
                        </div>
                        <span className="text-sm font-semibold dark:text-white text-slate-900">{formatCurrency(breakdown.hoa)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded-full bg-red-500"></div>
                            <span className="text-sm dark:text-slate-300 text-slate-600">Other Debts</span>
                        </div>
                        <span className="text-sm font-semibold dark:text-white text-slate-900">{formatCurrency(breakdown.otherDebts)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PropertyInfo;
